import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Alert, Button, Card, Checkbox, Row, Modal, Popconfirm, Form, message } from "antd";
import { connect } from "dva";
import moment from "moment";
import { Link } from "umi";
import { COMPANY_DEALERS_TERMS_OF_USE_URL } from "@/utils/constants";
import { ShipmentStatus, QuoteStatus } from "@/utils/enum";
import { getSingleShipment } from "@/services/shipments";
import VehiclesList from "@/components/VehiclesList";
import AddVehicle from "./AddVehicle";
import QuoteVehicle from "./QuoteVehicle";
import VehicleActionsButton from "./VehicleMenu";

const { confirm } = Modal;

const checkAllVehiclesQuoted = vehicles => {
  const currentVehicles = vehicles || [];
  return (
    currentVehicles.length > 0 &&
    currentVehicles.every(vehicle => vehicle?.quote?.status === QuoteStatus.Completed && !vehicle?.quote?.expired)
  );
};

// checks for any vehicles that have not had a price request
const checkVehiclesMissingPriceRequest = vehicles =>
  (vehicles || []).some(vehicle => vehicle?.quote?.status === QuoteStatus.None);

// checks for any vehicles that have an active price request
const checkVehiclesActivePriceRequest = vehicles =>
  (vehicles || []).some(vehicle => vehicle?.quote?.status === QuoteStatus.Pending);

// check if the quote for this vehicle is expired
export const checkVehicleExpiredQuote = vehicle => {
  if (vehicle?.quote?.expired) {
    return true;
  }

  if (vehicle?.quote?.resolvedAt) {
    const checkResolvedAt = moment(vehicle?.quote?.resolvedAt).add(2, "weeks");
    const currentTime = moment();
    if (currentTime.diff(checkResolvedAt) > 0) {
      return true;
    }
  }

  return false;
};

// checks for any vehicles that have expired quotes
const checkVehiclesExpiredQuotes = vehicles => (vehicles || []).some(vehicle => checkVehicleExpiredQuote(vehicle));

// checks the vehicles in the other shipment in this dealer exchange (if applicable) to see if they are all quoted
const checkVehiclesQuoteStatusInExchangeShipment = shipmentData => {
  const { childShipmentId, parentShipmentId } = shipmentData ?? {};
  // Not part of exchange
  if (!childShipmentId && !parentShipmentId) {
    return null;
  }

  getSingleShipment(childShipmentId || parentShipmentId)
    .then(exchangeShipmentData => {
      const { vehicles, status } = exchangeShipmentData ?? {};

      if (status < ShipmentStatus.Pending) return QuoteStatus.None;
      if (checkVehiclesExpiredQuotes(vehicles)) return QuoteStatus.Expired;
      if (checkVehiclesMissingPriceRequest(vehicles)) return QuoteStatus.None;
      if (checkVehiclesActivePriceRequest(vehicles)) return QuoteStatus.Pending;
      if (checkAllVehiclesQuoted(vehicles)) return QuoteStatus.Completed;

      return null;
    })
    .catch(err => {
      console.error(err);
      message.error("Error checking exchange shipment");

      return null;
    });

  return null;
};

const VehicleListContainer = ({
  shipmentData,
  loadingPostShipment,
  dispatch,
  currentUser,
  quotesUpdated,
  configPerm,
}) => {
  const { vehicles } = shipmentData;

  const allVehiclesQuoted = useMemo(() => checkAllVehiclesQuoted(vehicles), [vehicles]);
  const vehiclesMissingPriceRequest = useMemo(() => checkVehiclesMissingPriceRequest(vehicles), [vehicles]);
  const vehiclesActivePriceRequest = useMemo(() => checkVehiclesActivePriceRequest(vehicles), [vehicles]);
  const vehiclesExpiredQuotes = useMemo(() => checkVehiclesExpiredQuotes(vehicles), [vehicles]);
  const exchangeShipmentQuoteVehiclesStatus = useMemo(
    () => checkVehiclesQuoteStatusInExchangeShipment(shipmentData),
    [shipmentData],
  );
  const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState(false);
  const [termsAndConditionsPopConfirmVisible, setTermsAndConditionsPopConfirmVisible] = useState(false);
  const [confirmedWillDoublePostForExchange, setConfirmedWillDoublePostForExchange] = useState(false);

  const getVehiclesExtraButtons = () => {
    const extraButtonList = [];

    const aVehicleNeedsQuote =
      shipmentData?.vehicles?.filter(vehicle => vehicle?.quote?.status === QuoteStatus.None).length > 0;
    const canQuote = aVehicleNeedsQuote && shipmentData.status <= ShipmentStatus.Pending;
    const canAdd = shipmentData.status < ShipmentStatus.Posted;

    if (canQuote && configPerm["CoreService.Shipments.Vehicles.Request"]) {
      extraButtonList.push(<QuoteVehicle key="quote-vehicles" />);
    }

    if (canAdd && configPerm["CoreService.Shipments.Vehicles.Create"]) {
      extraButtonList.push(<AddVehicle key="add-vehicle" />);
    }

    return extraButtonList;
  };

  const confirmWillDoublePostForExchange = () => {
    confirm({
      title: "Post Dealer Trade",
      content: "Posting this shipment will post both shipments in this dealer trade. Are you sure?",
      onOk() {
        setConfirmedWillDoublePostForExchange(true);
      },
      okText: "Yes",
      cancelText: "No",
    });
  };

  const handleClickPostMyShipment = useCallback(() => {
    const exchangeShipmentId = shipmentData.childShipmentId || shipmentData.parentShipmentId || null;

    if (exchangeShipmentId && !confirmedWillDoublePostForExchange) {
      confirmWillDoublePostForExchange();
      return;
    }

    dispatch({
      type: "shipmentDetails/confirmSingleShipment",
      shipmentId: shipmentData.id,
    }).then(res => {
      if (!res.error) {
        Modal.success({
          content:
            "Your shipment has been successfully posted. We will notify you when it gets assigned to a transporter.",
          style: { fontWeight: "bold" },
        });
      }
    });
  }, [dispatch, shipmentData, confirmedWillDoublePostForExchange]);

  useEffect(() => {
    if (confirmedWillDoublePostForExchange) {
      handleClickPostMyShipment();
      setConfirmedWillDoublePostForExchange(false);
    }
  }, [confirmedWillDoublePostForExchange, handleClickPostMyShipment]);

  const postShipmentSection = useMemo(() => {
    const exchangeVehiclesQuoted =
      exchangeShipmentQuoteVehiclesStatus === null || exchangeShipmentQuoteVehiclesStatus === QuoteStatus.Completed;

    if (
      !allVehiclesQuoted ||
      shipmentData.status !== ShipmentStatus.Completed ||
      shipmentData.isInternalShipment ||
      !exchangeVehiclesQuoted ||
      !configPerm["CoreService.Shipments.Post"]
    ) {
      return null;
    }

    return (
      <Form>
        <Row justify="end" style={{ marginTop: 24 }}>
          <Form.Item valuePropName="checked">
            <Checkbox
              style={{ textAlign: "end" }}
              checked={termsAndConditionsAccepted}
              onChange={event => {
                setTermsAndConditionsAccepted(event.target.checked);
              }}
            >
              I agree to{" "}
              <a href={COMPANY_DEALERS_TERMS_OF_USE_URL} target="_blank" rel="noreferrer">
                terms and conditions
              </a>{" "}
              and authorize Autosled to electronically debit my account and, if necessary, electronically credit my
              account to correct erroneous debits.
            </Checkbox>
          </Form.Item>
        </Row>
        <Row justify="end" style={{ marginTop: 24 }}>
          {shipmentData.status === ShipmentStatus.Completed && (
            <Form.Item>
              <Popconfirm
                title="You must accept the terms and conditions first."
                placement="topRight"
                htmlType="submit"
                open={termsAndConditionsPopConfirmVisible}
                onConfirm={() => setTermsAndConditionsPopConfirmVisible(false)}
                cancelButtonProps={{ style: { display: "none" } }}
              >
                <Button
                  type="primary"
                  loading={loadingPostShipment}
                  style={{
                    float: "right",
                  }}
                  onClick={() => {
                    if (termsAndConditionsAccepted) {
                      handleClickPostMyShipment();
                    } else {
                      setTermsAndConditionsPopConfirmVisible(true);
                    }
                  }}
                >
                  Post My Shipment
                </Button>
              </Popconfirm>
            </Form.Item>
          )}
        </Row>
      </Form>
    );
  }, [
    loadingPostShipment,
    allVehiclesQuoted,
    shipmentData,
    termsAndConditionsAccepted,
    exchangeShipmentQuoteVehiclesStatus,
    termsAndConditionsPopConfirmVisible,
    handleClickPostMyShipment,
    configPerm,
  ]);

  const showMissingQuotesAlert = useMemo(
    () => vehiclesMissingPriceRequest && vehicles?.length > 0,
    [vehiclesMissingPriceRequest, vehicles],
  );

  const renderItemExtra = useCallback(
    item => (
      <VehicleActionsButton
        vehicleData={item}
        shipmentData={shipmentData}
        dispatch={dispatch}
        currentUser={currentUser}
      />
    ),
    [shipmentData, dispatch, currentUser],
  );

  return (
    <Card style={{ marginBottom: 16 }}>
      <VehiclesList
        pricesSpacing={1}
        updatedQuotes={quotesUpdated}
        vehicles={shipmentData?.vehicles || []}
        priceExtractor={item => item?.quote?.dealerPrice}
        shipmentData={shipmentData}
        extra={getVehiclesExtraButtons()}
        subHeader={
          shipmentData.status < ShipmentStatus.Posted && (
            <>
              {exchangeShipmentQuoteVehiclesStatus === QuoteStatus.None && (
                <Alert
                  showIcon
                  type="warning"
                  message="Vehicles missing quotes."
                  description={
                    <div>
                      {`One or more vehicles in the other shipment in this exchange needs pricing before both shipments in the exchange can be posted. Go to the other shipment `}
                      <Link
                        to={`/shipments/dealer/all/view?shipmentId=${
                          shipmentData.childShipmentId || shipmentData.parentShipmentId
                        }`}
                      >
                        here
                      </Link>
                    </div>
                  }
                  style={{ marginBottom: 24 }}
                />
              )}

              {exchangeShipmentQuoteVehiclesStatus === QuoteStatus.Pending && (
                <Alert
                  showIcon
                  type="info"
                  message="Vehicles pending quotes."
                  description="One or more vehicles in the other shipment in this exchange has pending price requests. All vehicles in both shipments must be priced before the exchange can be posted."
                  style={{ marginBottom: 24 }}
                />
              )}

              {showMissingQuotesAlert && (
                <Alert
                  showIcon
                  type="warning"
                  message="Vehicles missing quotes."
                  description='One or more vehicles in this shipment needs pricing before it can be posted. Click the "Request Prices" button to request prices.'
                  style={{ marginBottom: 24 }}
                />
              )}

              {vehiclesActivePriceRequest && (
                <Alert
                  showIcon
                  type="info"
                  message="Vehicles pending quotes."
                  description="We have received your price requests and will get back to you shortly."
                  style={{ marginBottom: 24 }}
                />
              )}

              {vehiclesExpiredQuotes && exchangeShipmentQuoteVehiclesStatus === QuoteStatus.Expired && (
                <Alert
                  showIcon
                  type="error"
                  message="Expired quotes."
                  description='Quotes have expired for one or more vehicles in this shipment. Please click the "Request Prices" button to request new prices.'
                  style={{ marginBottom: 24 }}
                />
              )}

              {allVehiclesQuoted && exchangeShipmentQuoteVehiclesStatus === QuoteStatus.Completed && (
                <Alert
                  message="All vehicles have been quoted."
                  description={
                    !shipmentData.isInternalShipment &&
                    'This shipment is ready to be posted to Autosled\'s public marketplace. Click the "Post My Shipment" button to continue.'
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}
            </>
          )
        }
        itemExtra={renderItemExtra}
      />

      {postShipmentSection}
    </Card>
  );
};

export default connect(({ user, shipmentDetails, loading, configs }) => ({
  shipmentData: shipmentDetails.data,
  quotesUpdated: shipmentDetails.quotesUpdated,
  currentUser: user.currentUser,
  loadingPostShipment: loading.effects["shipmentDetails/confirmSingleShipment"],
  configPerm: configs.configPerm,
}))(VehicleListContainer);
